import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Tag as TagIcon } from "lucide-react";
import { useState } from "react";
import type { Product, Variant } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { BulkProgress, BulkReport } from "@/components/table/BulkReport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBulkRun } from "@/components/table/useBulkRun";
import { describeRejection } from "@/modules/catalog/writeRejection";

/** A selected product paired with the variants its own HSN edit reaches. */
export interface BulkEditTarget {
  product: Product;
  variants: Variant[];
}

/** Bulk-set one HSN code across every variant of every selected product. */
function HsnBulkAction({ targets }: { targets: BulkEditTarget[] }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");

  const codesQuery = useQuery({
    queryKey: ["catalog", "hsn", "list-for-edit"],
    queryFn: () => data.catalog.listHsnCodes({ limit: 100 }),
    enabled: open,
  });

  const items = targets.flatMap((target) =>
    target.variants.map((variant) => ({ product: target.product, variant })),
  );

  const bulk = useBulkRun<(typeof items)[number]>((item) =>
    data.catalog.setVariantHsn(item.variant.id, code, {
      expectedUpdatedAt: item.variant.channelUpdatedAt,
    }),
  );

  async function apply() {
    await bulk.run(items);
    // Some of `items` may have failed; the ones that did not are already
    // sitting in Postgres by the time this resolves, so the screen has to
    // reflect them immediately rather than waiting for an unrelated
    // refetch to happen to run.
    void queryClient.invalidateQueries({ queryKey: ["catalog"] });
  }

  async function retry() {
    await bulk.retryFailed();
    void queryClient.invalidateQueries({ queryKey: ["catalog"] });
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setCode("");
          bulk.reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          Set HSN code
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3">
        <p className="text-sm font-medium">
          Set HSN code on {items.length} variant{items.length === 1 ? "" : "s"}
        </p>
        <Select value={code} onValueChange={setCode}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Choose an HSN code" />
          </SelectTrigger>
          <SelectContent>
            {(codesQuery.data?.items ?? []).map((entry) => (
              <SelectItem key={entry.id} value={entry.code}>
                {entry.code} — {entry.description} ({entry.gstRate}%)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          disabled={code === "" || bulk.running}
          onClick={() => {
            void apply();
          }}
        >
          {bulk.running ? <Loader2 className="animate-spin" /> : null}
          Apply
        </Button>
        {bulk.progress ? <BulkProgress progress={bulk.progress} /> : null}
        {bulk.outcomes && !bulk.progress ? (
          <BulkReport
            outcomes={bulk.outcomes}
            describe={describeRejection}
            labelOf={(item) => `${item.product.title} — ${item.variant.title}`}
            onRetry={() => {
              void retry();
            }}
            retrying={bulk.running}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** Bulk-add one tag to every selected product's tag list. */
function TagBulkAction({ targets }: { targets: BulkEditTarget[] }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tag, setTag] = useState("");

  const products = targets.map((target) => target.product);

  const bulk = useBulkRun<Product>((product) =>
    data.catalog.updateProduct(
      product.id,
      // Deduplicated rather than appended blindly — running the same
      // bulk add twice on an overlapping selection must not leave a
      // product carrying the same tag twice.
      { tags: [...new Set([...product.tags, tag])] },
      { expectedUpdatedAt: product.channelUpdatedAt },
    ),
  );

  async function apply() {
    await bulk.run(products);
    void queryClient.invalidateQueries({ queryKey: ["catalog"] });
  }

  async function retry() {
    await bulk.retryFailed();
    void queryClient.invalidateQueries({ queryKey: ["catalog"] });
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setTag("");
          bulk.reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          <TagIcon />
          Add tag
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3">
        <p className="text-sm font-medium">
          Add a tag to {targets.length} product{targets.length === 1 ? "" : "s"}
        </p>
        <div className="space-y-1">
          <Label htmlFor="bulk-tag">Tag</Label>
          <Input
            id="bulk-tag"
            value={tag}
            onChange={(event) => {
              setTag(event.target.value);
            }}
            placeholder="e.g. B12"
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={tag.trim() === "" || bulk.running}
          onClick={() => {
            void apply();
          }}
        >
          {bulk.running ? <Loader2 className="animate-spin" /> : null}
          Apply
        </Button>
        {bulk.progress ? <BulkProgress progress={bulk.progress} /> : null}
        {bulk.outcomes && !bulk.progress ? (
          <BulkReport
            outcomes={bulk.outcomes}
            describe={describeRejection}
            labelOf={(product) => product.title}
            onRetry={() => {
              void retry();
            }}
            retrying={bulk.running}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The catalogue's bulk actions (FR-CAT-8, M6-5), for the shared selection
 * bar (#634), which holds the count, the select-all and Clear.
 *
 * HSN and tags are two independent actions rather than one combined form:
 * FR-CAT-8 requires each change written through on its own, and a single
 * "Apply" button covering both fields would either send them as one write
 * (losing that guarantee) or silently send two writes behind one click that
 * only reports one result.
 *
 * Gated on `catalog.product.bulkEdit` by the list, separately from
 * `catalog.variant.editHsn` and `catalog.product.edit`, which gate the
 * single-item writes this calls underneath — hiding is a courtesy; the real
 * gate is server-side on those same routes.
 */
export function CatalogBulkActions({ targets }: { targets: BulkEditTarget[] }) {
  return (
    <>
      <HsnBulkAction targets={targets} />
      <TagBulkAction targets={targets} />
    </>
  );
}
